/**
 * CommentsPanel — list of annotation threads (anchored, resolvable).
 *
 * Mirrors LayersPanel: purely presentational. All comment state lives in the
 * editor and is mutated through the provided callbacks. Each row shows the
 * author, body, anchor kind, replies, and resolve / delete actions, plus a
 * composer to add a new comment and a reply box per thread.
 */

import { useState } from 'react'
import {
  MessageSquare,
  Check,
  RotateCcw,
  Trash2,
  X,
  CornerDownRight,
  Plus,
} from 'lucide-react'
import type { DiagramComment } from '../../types/schema'
import './CommentsPanel.css'

interface CommentsPanelProps {
  comments: DiagramComment[]
  /** Currently-focused thread (highlighted; clicking a pin focuses it). */
  activeCommentId?: string | null
  onFocus?: (id: string) => void
  onAdd: (text: string) => void
  onToggleResolved: (id: string) => void
  onDelete: (id: string) => void
  onReply: (id: string, text: string) => void
  onClose?: () => void
}

export default function CommentsPanel({
  comments,
  activeCommentId,
  onFocus,
  onAdd,
  onToggleResolved,
  onDelete,
  onReply,
  onClose,
}: CommentsPanelProps) {
  const [draft, setDraft] = useState('')
  const [replyFor, setReplyFor] = useState<string | null>(null)
  const [replyDraft, setReplyDraft] = useState('')
  const [showResolved, setShowResolved] = useState(true)

  const visible = showResolved ? comments : comments.filter((c) => !c.resolved)
  const openCount = comments.filter((c) => !c.resolved).length

  const submitNew = () => {
    const text = draft.trim()
    if (!text) return
    onAdd(text)
    setDraft('')
  }

  const submitReply = (id: string) => {
    const text = replyDraft.trim()
    if (!text) return
    onReply(id, text)
    setReplyDraft('')
    setReplyFor(null)
  }

  return (
    <div className="comments-panel">
      <div className="panel-header">
        <h3>
          <MessageSquare size={15} style={{ marginRight: 6, verticalAlign: '-2px' }} />
          Comments <span className="comments-count">{openCount}</span>
        </h3>
        <div className="comments-header-actions">
          <button
            type="button"
            className={`comments-filter-btn${showResolved ? ' active' : ''}`}
            onClick={() => setShowResolved((v) => !v)}
            title={showResolved ? 'Hide resolved' : 'Show resolved'}
          >
            {showResolved ? 'All' : 'Open'}
          </button>
          {onClose && (
            <button type="button" className="icon-btn" onClick={onClose} title="Close comments">
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      <div className="comments-composer">
        <textarea
          className="comments-textarea"
          placeholder="Add a comment…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submitNew()
          }}
          rows={2}
        />
        <button type="button" className="comments-add-btn" onClick={submitNew} disabled={!draft.trim()}>
          <Plus size={14} /> Comment
        </button>
      </div>

      <div className="comments-list">
        {visible.length === 0 && (
          <div className="comments-empty">No comments yet. Add one above.</div>
        )}
        {visible.map((c) => {
          const isActive = c.id === activeCommentId
          return (
            <div
              key={c.id}
              className={`comments-row${c.resolved ? ' resolved' : ''}${isActive ? ' active' : ''}`}
              onClick={() => onFocus?.(c.id)}
            >
              <div className="comments-row-head">
                <span className="comments-author">{c.author}</span>
                <span className="comments-anchor-kind">
                  {c.nodeId ? 'on node' : 'on canvas'}
                </span>
                <div className="comments-row-actions">
                  <button
                    type="button"
                    className="icon-btn"
                    onClick={(e) => {
                      e.stopPropagation()
                      onToggleResolved(c.id)
                    }}
                    title={c.resolved ? 'Reopen' : 'Resolve'}
                  >
                    {c.resolved ? <RotateCcw size={14} /> : <Check size={14} />}
                  </button>
                  <button
                    type="button"
                    className="icon-btn"
                    onClick={(e) => {
                      e.stopPropagation()
                      onDelete(c.id)
                    }}
                    title="Delete comment"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              <div className="comments-body">{c.text}</div>

              {c.replies && c.replies.length > 0 && (
                <div className="comments-replies">
                  {c.replies.map((r) => (
                    <div key={r.id} className="comments-reply">
                      <CornerDownRight size={12} className="comments-reply-icon" />
                      <span className="comments-reply-author">{r.author}</span>
                      <span className="comments-reply-text">{r.text}</span>
                    </div>
                  ))}
                </div>
              )}

              {replyFor === c.id ? (
                <div className="comments-reply-composer" onClick={(e) => e.stopPropagation()}>
                  <input
                    className="comments-reply-input"
                    value={replyDraft}
                    autoFocus
                    placeholder="Reply…"
                    onChange={(e) => setReplyDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') submitReply(c.id)
                      if (e.key === 'Escape') setReplyFor(null)
                    }}
                  />
                  <button
                    type="button"
                    className="comments-reply-send"
                    onClick={() => submitReply(c.id)}
                  >
                    Send
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="comments-reply-trigger"
                  onClick={(e) => {
                    e.stopPropagation()
                    setReplyFor(c.id)
                    setReplyDraft('')
                  }}
                >
                  Reply
                </button>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
